import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, CheckCircle, Package, Users, FileText } from 'lucide-react';
import { apiService } from '../../service/Api';
import { useAuth } from '../../context/AuthContext';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Tailspin! 🎉',
    description: 'Let\'s take a quick tour to help you get started. We\'ll walk you through creating your first sales collateral in just a few steps.',
    position: 'center',
    action: null,
    highlightElement: null,
  },
  {
    id: 'products',
    title: 'Step 1: Create a Product',
    description: 'First, let\'s add a product to your catalog. Click on the "Products" card to open the products panel.',
    position: 'top-left',
    action: 'Click on Products Card',
    highlightElement: 'products-card',
    waitForAction: true,
  },
  {
    id: 'products-drawer',
    title: 'Add Your First Product',
    description: 'Great! Now click the "New Product" button in the drawer to create a product. Fill in the details about what you\'re selling.',
    position: 'left',
    action: 'Create a Product',
    highlightElement: 'products-panel',
    waitForPanel: 'products',
  },
  {
    id: 'prospects',
    title: 'Step 2: Create a Prospect',
    description: 'Next, let\'s add a target company (prospect). Click on the "Prospects" card to open the prospects panel.',
    position: 'top',
    action: 'Click on Prospects Card',
    highlightElement: 'prospects-card',
    waitForAction: true,
    requiresProductCreated: true,
  },
  {
    id: 'prospects-drawer',
    title: 'Add Your First Prospect',
    description: 'Perfect! Now click "New Prospect" to add a target company. Enter their details so we can personalize your collateral.',
    position: 'left',
    action: 'Create a Prospect',
    highlightElement: 'prospects-panel',
    waitForPanel: 'prospects',
  },
  {
    id: 'create-collateral',
    title: 'Step 3: Generate Collateral',
    description: 'Excellent! Now let\'s create your first sales collateral. Click the "Create Collateral" button.',
    position: 'bottom-left',
    action: 'Click Create Collateral',
    highlightElement: 'create-collateral-btn',
    waitForAction: true,
    requiresProspectCreated: true,
  },
  {
    id: 'collateral-form',
    title: 'Fill Out Collateral Details',
    description: 'On the next page, select your product, prospect, collateral type, and provide a description of what you want to create. Then click "Generate Collateral".',
    position: 'center',
    action: 'Complete on Collateral Page',
    navigateTo: '/tailspin/collateral',
  },
  {
    id: 'complete',
    title: '🎊 Tour Complete!',
    description: 'You\'re all set! You now know how to create products, prospects, and generate AI-powered sales collateral. Click "Finish Tour" to start using Tailspin.',
    position: 'center',
    action: 'Finish Tour',
    highlightElement: null,
  },
];

export default function ProductTour({ onComplete, onSkip }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [completing, setCompleting] = useState(false);
  const { checkAuthStatus } = useAuth();

  const currentStep = TOUR_STEPS[currentStepIndex];

  useEffect(() => {
    // Add tour-active class to body to prevent interactions outside the tour
    document.body.classList.add('tour-active');
    return () => {
      document.body.classList.remove('tour-active');
    };
  }, []);

  const handleNext = () => {
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleSkip = () => {
    if (window.confirm('Are you sure you want to skip the tour? You can always explore features on your own.')) {
      setIsVisible(false);
      if (onSkip) onSkip();
    }
  };

  const handleComplete = async () => {
    try {
      setCompleting(true);
      // Call the API to mark onboarding as complete
      const response = await apiService.completeOnboarding();
      
      if (response.data.success) {
        // Refresh auth status to update user state
        await checkAuthStatus();
        setIsVisible(false);
        if (onComplete) onComplete();
      }
    } catch (error) {
      console.error('Error completing tour:', error);
      alert('Failed to complete tour. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  // Get position styles based on step position
  const getTooltipPosition = () => {
    switch (currentStep.position) {
      case 'center':
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        };
      case 'top-left':
        return {
          top: '20%',
          left: '20%',
        };
      case 'top':
        return {
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'bottom-left':
        return {
          bottom: '20%',
          left: '20%',
        };
      case 'left':
        return {
          top: '50%',
          left: '5%',
          transform: 'translateY(-50%)',
        };
      default:
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        };
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Spotlight effect for highlighted elements */}
        {currentStep.highlightElement && (
          <div
            id="tour-spotlight"
            style={{
              position: 'absolute',
              pointerEvents: 'none',
              border: '3px solid #FF6B5A',
              borderRadius: '12px',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 20px rgba(255, 107, 90, 0.5)',
              transition: 'all 0.3s ease',
            }}
          />
        )}

        {/* Tour Tooltip */}
        <motion.div
          key={currentStep.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          style={{
            position: 'absolute',
            ...getTooltipPosition(),
            maxWidth: '500px',
            width: '90%',
            backgroundColor: '#F8F7F4',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '2px solid #D7B472',
            zIndex: 10000,
          }}
        >
          {/* Close Button */}
          <button
            onClick={handleSkip}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#0A0A10',
              opacity: 0.5,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
          >
            <X size={24} />
          </button>

          {/* Progress Indicator */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {TOUR_STEPS.map((_, index) => (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    height: '4px',
                    backgroundColor: index <= currentStepIndex ? '#FF6B5A' : '#D7B472',
                    opacity: index <= currentStepIndex ? 1 : 0.3,
                    borderRadius: '2px',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>
            <p style={{ fontSize: '14px', color: '#0A0A10', opacity: 0.7, margin: 0 }}>
              Step {currentStepIndex + 1} of {TOUR_STEPS.length}
            </p>
          </div>

          {/* Icon */}
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 107, 90, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
            }}
          >
            {currentStep.id === 'welcome' && <CheckCircle size={32} color="#FF6B5A" />}
            {currentStep.id.includes('product') && <Package size={32} color="#FF6B5A" />}
            {currentStep.id.includes('prospect') && <Users size={32} color="#D7B472" />}
            {currentStep.id.includes('collateral') && <FileText size={32} color="#FF6B5A" />}
            {currentStep.id === 'complete' && <CheckCircle size={32} color="#D7B472" />}
          </div>

          {/* Content */}
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0A0A10', marginBottom: '12px' }}>
            {currentStep.title}
          </h2>
          <p style={{ fontSize: '16px', color: '#0A0A10', opacity: 0.8, marginBottom: '32px', lineHeight: '1.6' }}>
            {currentStep.description}
          </p>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
            <button
              onClick={handlePrevious}
              disabled={currentStepIndex === 0}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: '2px solid #D7B472',
                backgroundColor: 'transparent',
                color: '#0A0A10',
                fontWeight: '600',
                cursor: currentStepIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: currentStepIndex === 0 ? 0.3 : 1,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (currentStepIndex !== 0) {
                  e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentStepIndex !== 0) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              Previous
            </button>

            {currentStep.id === 'complete' ? (
              <button
                onClick={handleComplete}
                disabled={completing}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#FF6B5A',
                  color: 'white',
                  fontWeight: '600',
                  cursor: completing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: completing ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!completing) e.currentTarget.style.backgroundColor = '#e55a4a';
                }}
                onMouseLeave={(e) => {
                  if (!completing) e.currentTarget.style.backgroundColor = '#FF6B5A';
                }}
              >
                {completing ? 'Completing...' : 'Finish Tour'}
                {!completing && <CheckCircle size={20} />}
              </button>
            ) : (
              <button
                onClick={handleNext}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#FF6B5A',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
              >
                {currentStep.action || 'Next'}
                <ArrowRight size={20} />
              </button>
            )}
          </div>

          {/* Skip Tour Link */}
          {currentStep.id !== 'complete' && (
            <button
              onClick={handleSkip}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '8px',
                background: 'transparent',
                border: 'none',
                color: '#0A0A10',
                opacity: 0.6,
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
            >
              Skip tour and explore on my own
            </button>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

